import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Alert,
  Divider,
  CircularProgress,
  Container
} from '@mui/material';
import { Delete, Save, Cancel } from '@mui/icons-material';
import { salesAPI } from '../../services/API';
import { useSnackbar } from 'notistack';

export const EditSale = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cart, setCart] = useState([]);

  useEffect(() => {
    fetchSale();
  }, [id]);

  const fetchSale = async () => {
    try {
      const response = await salesAPI.get(id);
      setSale(response.data.data.salesInvoice);
      setCart(response.data.data.salesInvoice.items.map(item => ({
        ...item,
        medicine: item.medicine,
        batchStock: item.batchStock?._id
      })));
    } catch (error) {
      enqueueSnackbar('Error fetching sale', { variant: 'error' });
      navigate('/hospital/pharmacy/sales');
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = (index, quantity) => {
    if (quantity < 1) return;

    const updatedCart = [...cart];
    updatedCart[index].quantity = quantity;
    // Recalculate item total
    updatedCart[index].totalAmount = calculateItemTotal(updatedCart[index]);
    setCart(updatedCart);
  };

  const updateSellingPrice = (index, price) => {
    const updatedCart = [...cart];
    updatedCart[index].sellingPrice = parseFloat(price) || 0;
    updatedCart[index].totalAmount = calculateItemTotal(updatedCart[index]);
    setCart(updatedCart);
  };

  const calculateItemTotal = (item) => {
    const itemTotal = item.quantity * item.sellingPrice;
    const itemDiscount = itemTotal * (item.discount / 100);
    return itemTotal - itemDiscount;
  };

  const calculateTotals = () => {
    const subtotal = cart.reduce((sum, item) => sum + item.totalAmount, 0);
    const tax = subtotal * 0.18;
    const grandTotal = subtotal + tax;
    return { subtotal, tax, grandTotal };
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { subtotal, tax, grandTotal } = calculateTotals();

      const updateData = {
        items: cart.map(item => ({
          medicine: item.medicine._id,
          batchStock: item.batchStock,
          quantity: item.quantity,
          sellingPrice: item.sellingPrice,
          discount: item.discount,
          taxRate: 18
        })),
        subtotal,
        taxAmount: tax,
        grandTotal,
        dueAmount: Math.max(0, grandTotal - sale.paidAmount)
      };

      await salesAPI.update(id, updateData);
      enqueueSnackbar('Sale updated successfully', { variant: 'success' });
      navigate('/hospital/pharmacy/sales');
    } catch (error) {
      enqueueSnackbar('Error updating sale', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <CircularProgress size={32} />;
  if (!sale) return <Alert severity="error" sx={{ fontSize: '1.1rem' }}>Sale not found</Alert>;

  const { subtotal, tax, grandTotal } = calculateTotals();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f9fafc 0%, #f2f5f8 100%)',
      }}
    >
      <Container maxWidth="xl" sx={{ py: 5 }}>
        <Box>
          <Typography variant="h3" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '2rem', md: '2.5rem' } }}>
            Edit Sale #{sale.invoiceNumber}
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h5" gutterBottom sx={{ fontSize: '1.5rem' }}>
                    Edit Items
                  </Typography>

                  <TableContainer component={Paper} variant="outlined">
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Medicine</TableCell>
                          <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Price</TableCell>
                          <TableCell align="center" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Qty</TableCell>
                          <TableCell align="right" sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}>Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cart.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>
                              <Typography variant="subtitle1" sx={{ fontSize: '1.1rem' }}>
                                {item.medicine.name}
                              </Typography>
                              <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1rem' }}>
                                Batch: {item.batchNumber}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                size="small"
                                type="number"
                                value={item.sellingPrice}
                                onChange={(e) => updateSellingPrice(index, e.target.value)}
                                sx={{ width: 100 }}
                                inputProps={{ 
                                  style: { fontSize: '1.1rem', textAlign: 'center' } 
                                }}
                              />
                            </TableCell>
                            <TableCell align="center">
                              <TextField
                                size="small"
                                type="number"
                                value={item.quantity}
                                onChange={(e) => updateQuantity(index, parseInt(e.target.value))}
                                sx={{ width: 90 }}
                                inputProps={{ 
                                  style: { fontSize: '1.1rem', textAlign: 'center' } 
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="subtitle1" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                                ₹{item.totalAmount.toFixed(2)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ position: 'sticky', top: 100 }}>
                <CardContent>
                  <Typography variant="h5" gutterBottom sx={{ fontSize: '1.5rem' }}>
                    Summary
                  </Typography>

                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography sx={{ fontSize: '1.1rem' }}>Subtotal:</Typography>
                      <Typography sx={{ fontSize: '1.1rem' }}>₹{subtotal.toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography sx={{ fontSize: '1.1rem' }}>Tax (18%):</Typography>
                      <Typography sx={{ fontSize: '1.1rem' }}>₹{tax.toFixed(2)}</Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="h6" sx={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
                        Total:
                      </Typography>
                      <Typography variant="h6" color="primary.main" sx={{ fontSize: '1.3rem', fontWeight: 'bold' }}>
                        ₹{grandTotal.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => navigate('/hospital/pharmacy/sales')}
                      startIcon={<Cancel sx={{ fontSize: '1.3rem' }} />}
                      sx={{ 
                        fontSize: '1.1rem',
                        py: 1.5
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleSave}
                      disabled={saving}
                      startIcon={<Save sx={{ fontSize: '1.3rem' }} />}
                      sx={{ 
                        fontSize: '1.1rem',
                        py: 1.5
                      }}
                    >
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};